/**
 * SITE ITEMS OUTSIDE THE BUILDING (2026-10-20, Matt: the site plan should show and label site
 * equipment "when placed outside on the site plan" — outside meaning "not contained inside the
 * building's walls"; a subpanel in the garage is never called out). One test, shared by the
 * plan cut (which marks the item's linework `outdoor` so the site plan shows it past its
 * family hide) and the site plan's labels (frontend siteItemLabels.js), so a label never
 * names something the plan hides.
 *
 * Inside = the item's point (an object's footprint centre, a wall fixture's mounted-face
 * point, a pool's interior point) falls within any building outline at the exterior face of
 * stud (heightDatum.ts#studFaceFootprint — the same outline the height datum measures, every
 * piece of 16 sq ft or more, so a detached garage counts as building). A meter on the outside
 * face of an exterior wall sits beyond the stud face, so it reads outside; a panel on the
 * inside face reads inside.
 */
import type { Pt2, ResolvedElement, Path2D } from '@aj/bim-kernel'
import { isSiteLabelKind, objectFootprintCorners, objectAnchorPoint, isPointFixtureKind, flattenPathPts } from '@aj/bim-kernel'
import { studFaceFootprint } from './heightDatum'
import { fixtureFacePlacement } from './categories/fixture/planSymbols'
import { clipperReady } from './clipper'

function inRing(p: Pt2, ring: Pt2[]): boolean {
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i], [xj, yj] = ring[j]
    if ((yi > p[1]) !== (yj > p[1]) && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) inside = !inside
  }
  return inside
}

function ringArea(r: Pt2[]): number {
  let a = 0
  for (let i = 0, j = r.length - 1; i < r.length; j = i++) a += r[j][0] * r[i][1] - r[i][0] * r[j][1]
  return a / 2
}

/** a point inside a closed ring (its centroid when that is inside, else the middle of the
 *  widest horizontal chord through the ring's mid-height) */
export function interiorPoint(ring: Pt2[]): Pt2 | null {
  if (ring.length < 3) return null
  let cx = 0, cy = 0
  for (const p of ring) { cx += p[0]; cy += p[1] }
  const c: Pt2 = [cx / ring.length, cy / ring.length]
  if (inRing(c, ring)) return c
  let y0 = Infinity, y1 = -Infinity
  for (const p of ring) { y0 = Math.min(y0, p[1]); y1 = Math.max(y1, p[1]) }
  const y = (y0 + y1) / 2
  const xs: number[] = []
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i], [xj, yj] = ring[j]
    if ((yi > y) !== (yj > y)) xs.push(xi + ((y - yi) * (xj - xi)) / (yj - yi))
  }
  xs.sort((a, b) => a - b)
  let best: Pt2 | null = null, bw = -1
  for (let i = 0; i + 1 < xs.length; i += 2) if (xs[i + 1] - xs[i] > bw) { bw = xs[i + 1] - xs[i]; best = [(xs[i] + xs[i + 1]) / 2, y] }
  return best
}

/** the item's point and (for an object or pool) its outline, or null when it has none */
export function siteItemGeometry(el: ResolvedElement, byId: Map<string, ResolvedElement>): { pt: Pt2; ring: Pt2[] | null } | null {
  if (el.category === 'object') {
    const c = objectFootprintCorners(el)
    if (!c || c.length < 4) return null
    return { pt: [(c[0][0] + c[2][0]) / 2, (c[0][1] + c[2][1]) / 2], ring: c }
  }
  if (el.category === 'fixture') {
    if (isPointFixtureKind(String(el.typeParams.fixtureKind ?? ''))) {
      const a = objectAnchorPoint(el)
      return a ? { pt: a, ring: null } : null
    }
    const host = el.hostId ? byId.get(el.hostId) : null
    if (!host || host.category !== 'wall') return null
    const pl = fixtureFacePlacement(host as never, Number(el.params.hostOffsetFt) || 0, el.params.sideFlip === true)
    if (!pl) return null
    // the fixture's own face, a little proud of the wall face it mounts on
    const out: Pt2 = [-Math.sin(pl.rotZ), Math.cos(pl.rotZ)]
    const d = Math.min(Number(el.typeParams.depthFt) || 0.33, 0.5) * 0.5
    return { pt: [pl.pt[0] + out[0] * d, pl.pt[1] + out[1] * d], ring: null }
  }
  if (el.category === 'door') {
    // a gate: its span along its fence / site wall (hostOffsetFt is the opening's centre),
    // a rectangle the gate's width by the host's thickness (a foot when it has none)
    const host = el.hostId ? byId.get(el.hostId) : null
    const hp = host?.params?.path as Path2D | null
    if (!hp?.nodes || hp.nodes.length < 2) return null
    const pts = flattenPathPts(hp)
    let s = Math.max(0, Number(el.params.hostOffsetFt) || 0)
    for (let i = 0; i + 1 < pts.length; i++) {
      const a = pts[i], b = pts[i + 1]
      const len = Math.hypot(b[0] - a[0], b[1] - a[1])
      if (len < 1e-9) continue
      if (s > len && i + 2 < pts.length) { s -= len; continue }
      const u: Pt2 = [(b[0] - a[0]) / len, (b[1] - a[1]) / len], n: Pt2 = [-u[1], u[0]]
      const t = Math.min(s, len)
      const c: Pt2 = [a[0] + u[0] * t, a[1] + u[1] * t]
      const hw = (Number(el.params.widthFt) || 3) / 2
      const ht = Math.max(Number((host as ResolvedElement & { assemblyThicknessFt?: number }).assemblyThicknessFt) || 0, 1) / 2
      const at = (x: number, y: number): Pt2 => [c[0] + u[0] * x + n[0] * y, c[1] + u[1] * x + n[1] * y]
      return { pt: c, ring: [at(-hw, -ht), at(hw, -ht), at(hw, ht), at(-hw, ht)] }
    }
    return null
  }
  if (el.category === 'pool') {
    const b = el.params.boundary as Path2D | null
    if (!b?.nodes || b.nodes.length < 3) return null
    const ring = flattenPathPts({ ...b, closed: true })
    const pt = interiorPoint(ring)
    return pt ? { pt, ring } : null
  }
  return null
}

const CACHE = new WeakMap<readonly ResolvedElement[], Set<string>>()

/** the ids of every labeled site item that sits outside the building's walls (cached per
 *  resolved element list). Before clipper loads, nothing reads as outside. */
export function outdoorSiteItemIds(els: readonly ResolvedElement[]): Set<string> {
  const hit = CACHE.get(els)
  if (hit) return hit
  const out = new Set<string>()
  const items = els.filter((e) => isSiteLabelKind(e))
  if (!items.length) { CACHE.set(els, out); return out }
  if (!clipperReady()) return out
  let rings: Pt2[][] = []
  try { rings = studFaceFootprint(els as ResolvedElement[], (e) => !e.phaseDemolished).filter((r) => r.length >= 3 && Math.abs(ringArea(r)) >= 16) } catch { rings = [] }
  const byId = new Map(els.map((e) => [e.id as string, e]))
  for (const el of items) {
    const g = siteItemGeometry(el, byId)
    if (!g) continue
    if (!rings.some((r) => inRing(g.pt, r))) out.add(el.id)
  }
  CACHE.set(els, out)
  return out
}

/**
 * Where the arrowhead lands (2026-10-20, Matt: "make the arrow heads touch the outer edge of the
 * element on the side the note is located on instead of pointing to the direct center"): the
 * first point where the line from the item's centre toward the note's bend leaves the item's
 * outline. No outline (a wall fixture), or the bend inside the outline: the centre.
 */
export function edgeToward(c: Pt2, toward: Pt2, ring: Pt2[] | null | undefined): Pt2 {
  if (!ring || ring.length < 3) return c
  const dx = toward[0] - c[0], dy = toward[1] - c[1]
  let best = Infinity
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const ax = ring[j][0], ay = ring[j][1], ex = ring[i][0] - ax, ey = ring[i][1] - ay
    const den = dx * ey - dy * ex
    if (Math.abs(den) < 1e-12) continue
    const t = ((ax - c[0]) * ey - (ay - c[1]) * ex) / den
    const u = ((ax - c[0]) * dy - (ay - c[1]) * dx) / den
    if (t > 1e-9 && t <= 1 && u >= 0 && u <= 1 && t < best) best = t
  }
  return Number.isFinite(best) ? [c[0] + dx * best, c[1] + dy * best] : c
}
